// Style
import mainStyle from "../../configProvisioning.module.scss";

// Packages
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { isEqual, isEmpty } from "lodash";
import { v4 as uuidv4 } from "uuid";

// Hook
import useDataSourceManager from "hooks/useDataSourceManager";

// Icon
import { ReactComponent as CheckedIcon } from "assets/images/icons/icn_checked.svg";
import { ReactComponent as UnCheckedIcon } from "assets/images/icons/icn_unchecked.svg";
import { ReactComponent as CheckChangedIcon } from "assets/images/icons/icn_check_changed.svg";
import icon_snmp from "assets/images/icons/icn_Info_solid.svg";
import icon_snmp_hover from "assets/images/icons/icn_info_solid_hover.svg";

// Components
import CollapseCard from "components/Collapse";
import { MenuSelect } from "components/Select/menuSelect";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { Loading } from 'components/Loading';
import { StyledBox } from '../StyledBox';
import { HeaderIcon } from '../HeaderIcon';

// Global const
import { formLimitation } from "const";

// Local const
import {
  defaultStateTypes,
  defaultPortTypes,
} from '../../const';

import {
  getProfileRequestParams,
  getInitProfileList,
  getIsVlanInvalid,
  checkPvid,
  getInputStyles,
  getLinkspeedList,
} from "../../util";
import MacWhitelistSection from '../MacWhitelistSection';
import RadiusProfileSection from '../RadiusProfileSection';
import AggregationConfirmModal from "pages/System/RealmSetting/components/Wired/PortManagement/Switches/Modal/AggregationConfirmModal";
import LeaveConfirmModal from "components/Drawer/Content/Settings/Profiles/Port/leaveConfirmModal";
import LinkModal from "components/LinkModal";
import useZustandStore from "zustandStore";
import { ReactComponent as ProColorIcon } from "assets/images/icons/icn-pro.svg";
import { ReactComponent as ProOrangeOutlineIcon } from "assets/images/icons/icn_Pro_orange_outline.svg";
import {
  getUpdatedAssociatedPorts,
  syncPortLabels,
  checkPortInOtherAggregateGroup,
  getAvailableAggregateGroupId
} from "./util/helper";
import {
  defaultOperationList,
  operationDefinition,
  accessPolicyTypeDefinition
} from "pages/System/RealmSetting/components/Wired/const";

const proIconStyle = {
  display: "inline-block",
  verticalAlign: "middle",
  marginLeft: 2,
  marginTop: -12,
  marginBottom: -12,
  flexShrink: 0,
};

const defaultAccessPolicyTypeList = [
  { label: "Disabled", value: "DISABLED" },
  { label: "Static MAC Whitelist", value: "STATIC" },
  { label: "Port Security Delete-on-time Mode", value: "DELETE_ON_TIME" },
  { label: "802.1X (Profile-based)", value: "8021X_PROFILE" }
];

const ContentSwitch = ({
  auth,
  alertInfo,
  setAlertInfo,
  defaultConfig,
  setDefaultConfig,
  selectedDrawerTarget,
  isOpenEditDrawer,
  showEditGeneral,
  closeCallback,
  macProfiles = [],
  radiusProfiles = [],
  scheduleProfiles = [],
  onGoToPage
}) => {
  const [editUuids] = useState(() => ({
    getProfileList: uuidv4()
  }));

  const navigate = useNavigate();
  const isUnityProSubscribed = useZustandStore((state) => state.isUnityProSubscribed);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const theme = useTheme();
  const { sendRequest, getData, dataMap } = useDataSourceManager();

  const legendItemStyle = { display: "flex", alignItems: "center", gap: 1 };

  const [showAggregationConfirmModal, setShowAggregationConfirmModal] = useState(false);
  const [leaveModalState, setLeaveModalState] = useState({
    open: false,
    profileType: "",
    page: ""
  });

  const handleLeaveConfirm = () => {
    setLeaveModalState(prev => ({ ...prev, open: false }));
    closeCallback();
    onGoToPage?.("profiles", leaveModalState.page);
  };

  const portBoxStyle = (status, theme) => {
    const base = {
      width: "28px",
      height: "28px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "4px",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
      userSelect: "none",
      boxSizing: "border-box",
    };

    if (status === "source") {
      return {
        ...base,
        backgroundColor: theme?.palette?.primary?.blueSecound || "#87A2E2",
        color: "#ffffff",
        cursor: "default",
      };
    }
    if (status === "destination" || status === "selected") {
      return {
        ...base,
        backgroundColor: theme?.palette?.primary?.main || "#172664",
        color: "#ffffff",
      };
    }
    if (status === "disabled") {
      return {
        ...base,
        backgroundColor: "#e9ecef",
        color: "#adb5bd",
        opacity: 0.6,
        cursor: "not-allowed",
      };
    }
    return {
      ...base,
      backgroundColor: "#ffffff",
      color: theme?.palette?.primary?.main || "#172664",
      border: "1px solid #DDDDDD",
      "&:hover": {
        borderColor: "#909399",
        backgroundColor: "#ebedf0",
      }
    };
  };

  const devicePorts = useMemo(() => {
    return defaultConfig.portConfigs || [];
  }, [defaultConfig.portConfigs]);

  const isPortInOtherAggregateGroup = (portItem) => {
    return checkPortInOtherAggregateGroup({
      portItem,
      selectedIndexList: [Number(selectedPort.portIndex)],
      switchesData: defaultConfig.portConfigs || []
    });
  };

  const [isLoading, setIsLoading] = useState(false);
  const [isEdited, setIsEdited] = useState({});
  const [isChanged, setIsChanged] = useState(false);

  const [originalSelectedPort, setOriginalSelectedPort] = useState(selectedDrawerTarget ? structuredClone(selectedDrawerTarget) : {});
  const [selectedPort, setSelectedPort] = useState(selectedDrawerTarget ? structuredClone(selectedDrawerTarget) : {});
  const isNeedCombineModel = useMemo(() => selectedPort?.isNeedCombineModel, [selectedPort]);

  const [profileList, setProfileList] = useState([]);
  const selectedProfile = useMemo(() => profileList.find(profile => profile.isSelected), [profileList]);
  const isProfileMode = useMemo(() => !selectedProfile?.isDefault && !!selectedProfile?.value, [selectedProfile?.value]);

  const isEditable = useMemo(() => !selectedProfile || selectedProfile?.isDefault, [selectedProfile]);
  const { isCopper, isFiber } = useMemo(() => selectedPort, [selectedPort]);

  const [linkSpeeds, setLinkSpeeds] = useState({
    copperList: getLinkspeedList({ speedList: selectedPort?.supportedLinkSpeedList?.copperList || [] }),
    fiberList: getLinkspeedList({ speedList: selectedPort?.supportedLinkSpeedList?.fiberList || [] })
  });

  const { vlanIdsCount, isVlanInvalid } = useMemo(() => getIsVlanInvalid({ ports: defaultConfig.portConfigs, selectedPort }), [defaultConfig.portConfigs, selectedPort]);

  const isSelectedPortSupportPoE = useMemo(() => {
    const { poePorts, portIndex } = selectedPort;

    if (isEmpty(poePorts) || portIndex == null) return false;
    return poePorts[portIndex -1] !== 0;

  }, [selectedPort?.poePorts, selectedPort?.portIndex]);

  const maxWiredSchedules = 8;
  const hasPortProfile = !!selectedProfile && !selectedProfile.isDefault;
  const profilePortScheduleOn = hasPortProfile && !!(selectedProfile.isPortScheduleMode ?? (selectedProfile.portState && selectedProfile.portSchedule));
  const profilePoeScheduleOn = hasPortProfile && !!(selectedProfile.isPoeScheduleMode ?? (selectedProfile.poeState && selectedProfile.poeSchedule));
  const wiredUsedSchedulesSet = useMemo(() => {
    const set = new Set();
    const profileSource = hasPortProfile ? {
      isPortScheduleMode: profilePortScheduleOn,
      portSchedule: selectedProfile.portSchedule,
      isPoeScheduleMode: profilePoeScheduleOn,
      poeSchedule: selectedProfile.poeSchedule,
    } : null;

    // Aggregate members are synchronized with the active port on save, so they count the live value too
    const liveIndices = new Set([Number(selectedPort?.portIndex)]);
    if (selectedPort?.operation?.type === "aggregation") {
      (selectedPort.operation.aggregatePort || []).forEach(idx => liveIndices.add(Number(idx)));
    }

    (defaultConfig.portConfigs || []).forEach((p) => {
      // The active port uses live selectedPort (or the chosen Port Profile) so UI changes are reflected immediately
      const source = liveIndices.has(Number(p.portIndex)) ? (profileSource || selectedPort) : p;
      if (source?.isPortScheduleMode && source?.portSchedule && source.portSchedule !== "-") set.add(source.portSchedule);
      // Only ports that support PoE count the PoE schedule
      const isPoeSupported = !isEmpty(p.poePorts) && p.poePorts[p.portIndex -1] !== 0;
      if (isPoeSupported && source?.isPoeScheduleMode && source?.poeSchedule && source.poeSchedule !== "-") set.add(source.poeSchedule);
    });

    return set;
  }, [defaultConfig.portConfigs, selectedPort, selectedProfile]);
  const wiredUsedCount = wiredUsedSchedulesSet.size;
  const scheduleOptionsWithLimit = useMemo(() => {
    if (!scheduleProfiles) return scheduleProfiles;
    return scheduleProfiles.map((opt) => {
      const val = opt?.value ?? opt;
      const disabled = wiredUsedCount >= maxWiredSchedules && !wiredUsedSchedulesSet.has(val);
      return { ...opt, disabled };
    });
  }, [scheduleProfiles, wiredUsedCount, wiredUsedSchedulesSet]);

  const isWiredScheduleOverLimit = wiredUsedCount > maxWiredSchedules;

  const renderScheduleUsageHint = () => (
    <>
      <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="caption" sx={{ color: theme.palette.primary.greyPrimary }}>
          {`Wired schedules in use: `}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
          {`${wiredUsedCount} / ${maxWiredSchedules}`}
        </Typography>
        <Tooltip
          title="Each device supports a maximum of 8 Profiles."
          arrow
          slotProps={{ tooltip: { sx: { maxWidth: "none", whiteSpace: "nowrap" } } }}
        >
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", width: 16, height: 16 }}>
            <img src={icon_snmp} alt="" style={{ width: 16, height: 16, cursor: "pointer" }} />
          </Box>
        </Tooltip>
      </Box>
      {isWiredScheduleOverLimit && (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: theme.palette.primary.redPrimary }}>
          No free slot on this switch for this profile.
        </Typography>
      )}
    </>
  );

  const getProfileList = () => {
    setIsLoading(true);
    const { apiUrl, requestParams } = getProfileRequestParams({ auth });

    sendRequest(
      apiUrl,
      editUuids.getProfileList,
      requestParams
    );
  };

  const onApply = () => {
    if (!isUnityProSubscribed) {
      const isAccessPort = isProfileMode
        ? selectedProfile?.portType === "access"
        : selectedPort?.portType === "access";
      const isAccessPolicyEnabled = isProfileMode
        ? selectedProfile?.accessPolicy?.enableState === true
        : selectedPort?.accessPolicy?.enableState === true;

      if (isAccessPort && isAccessPolicyEnabled) {
        setIsLinkModalOpen(true);
        return;
      }
    }

    if (selectedPort.operation?.type === "aggregation") {
      setShowAggregationConfirmModal(true);
    } else {
      handleProceedApply();
    }
  };

  const handleProceedApply = () => {
    const { portConfigs } = defaultConfig;

    const finalPortSchedule = selectedPort.isPortScheduleMode ? selectedPort.portSchedule : null;
    const finalPoeSchedule = selectedPort.isPoeScheduleMode ? selectedPort.poeSchedule : null;

    const finalAccessPolicy = selectedPort.accessPolicy ? {
      enableState: selectedPort.accessPolicy.enableState,
      type: selectedPort.accessPolicy.enableState ? selectedPort.accessPolicy.type : "STATIC",
      macList: (selectedPort.accessPolicy.enableState &&
        (selectedPort.accessPolicy.type === "STATIC" || selectedPort.accessPolicy.type === "DELETE_ON_TIME"))
        ? selectedPort.accessPolicy.macList : null,
      radiusProfile: (selectedPort.accessPolicy.enableState &&
        selectedPort.accessPolicy.type === "8021X_PROFILE")
        ? selectedPort.accessPolicy.radiusProfile : null,
      dynamicSize: (selectedPort.accessPolicy.enableState &&
        selectedPort.accessPolicy.type === "DELETE_ON_TIME")
        ? selectedPort.accessPolicy.dynamicSize : null,
    } : null;

    const activeIdx = Number(selectedPort.portIndex);
    const destinationPorts = (selectedPort.operation?.aggregatePort || []).map(Number);
    const finalAggregatePorts = selectedPort.operation?.type === "aggregation"
      ? [activeIdx, ...destinationPorts].filter(idx => idx !== activeIdx)
      : [];

    const updatedPort = {
      ...selectedPort,
      portName: isNeedCombineModel ? selectedPort.combinePortName : selectedPort.portName,
      portSchedule: finalPortSchedule,
      poeSchedule: finalPoeSchedule,
      accessPolicy: finalAccessPolicy,
      operation: {
        type: selectedPort.operation?.type || "switching",
        mirrorPort: selectedPort.operation?.mirrorPort || [],
        mirrorMode: selectedPort.operation?.mirrorMode || "",
        aggregatePort: finalAggregatePorts,
        aggregateType: selectedPort.operation?.aggregateType || "",
        aggregateGroupId: selectedPort.operation?.aggregateGroupId || null
      }
    };

    let tempPortConfigs = getUpdatedAssociatedPorts({
      switchesData: portConfigs,
      activeRow: selectedPort,
      originalRowData: originalSelectedPort,
      updatedPort,
      excludeIndices: [activeIdx]
    });

    tempPortConfigs = tempPortConfigs.map(port => {
      if (port.portIndex === activeIdx) {
        return syncPortLabels(updatedPort);
      }
      return port;
    });

    setDefaultConfig(prevConfig => {
      return {
        ...prevConfig,
        portConfigs: tempPortConfigs,
      }
    });

    closeCallback();
    setOriginalSelectedPort(selectedPort);
    setIsChanged(false);
  };

  const onCancel = () => {
    closeCallback();
  };

  const updatePorts = (field, value) => {
    setSelectedPort((prev) => ({
      ...prev,
      [field]: value
    }));

    if (!isEqual(originalSelectedPort[field], value)) {
      setIsEdited((prev) => ({ ...prev, [field]: true }));
    } else {
      setIsEdited((prev) => {
        const { [field]: removedField, ...rest } = prev;
        return rest;
      });
    }
  };

  const isFormInvalid = () => {
    let isInvalid = false;
    const { portName, portType, PVID, allowVlan, isPortScheduleMode, portSchedule, isPoeScheduleMode, poeSchedule, accessPolicy, operation } = selectedPort;

    if (portName === "") isInvalid = true;
    if (!checkPvid(PVID?.toString().trim())) isInvalid = true;
    if (isVlanInvalid) isInvalid = true;
    if (isWiredScheduleOverLimit) isInvalid = true;

    if (isPortScheduleMode && (!portSchedule || portSchedule === "-")) isInvalid = true;
    if (isPoeScheduleMode && (!poeSchedule || poeSchedule === "-")) isInvalid = true;

    if (operation?.type === "mirroring") {
      if (!operation.mirrorPort || operation.mirrorPort.length === 0) {
        isInvalid = true;
      }
    }

    if (operation?.type === "aggregation") {
      if (!operation.aggregatePort || operation.aggregatePort.length === 0) {
        isInvalid = true;
      }

      // aggregateGroupId 為 null 代表這台裝置已經沒有可用的 Aggregation 群組了
      // (getAvailableAggregateGroupId 在 capabilities.aggregation 額度用完時會回傳 null).
      if (operation.aggregateGroupId == null) {
        isInvalid = true;
      }

      // capabilities.aggregation 同時也是「每組最多幾個 port」的上限,
      // 群組成員數 = 自己這個 port + aggregatePort 裡的成員數.
      const maxAggregationPorts = selectedPort.aggregation;
      const currentGroupSize = 1 + (operation.aggregatePort?.length || 0);
      if (maxAggregationPorts != null && currentGroupSize > maxAggregationPorts) {
        isInvalid = true;
      }
    }

    if (portType === "access" && accessPolicy?.enableState) {
      const { type, macList, radiusProfile, dynamicSize } = accessPolicy;
      if (type === "STATIC") {
        if (!macList) isInvalid = true;
      } else if (type === "DELETE_ON_TIME") {
        if (!macList) isInvalid = true;
        if (dynamicSize === "" || dynamicSize == null) isInvalid = true;
        const sizeNum = Number(dynamicSize);
        if (isNaN(sizeNum) || sizeNum < 1 || sizeNum > 32) isInvalid = true;
      } else if (type === "8021X_PROFILE") {
        if (!radiusProfile) isInvalid = true;
      }
    }

    return isInvalid;
  };

  useEffect(() => {
    setOriginalSelectedPort(selectedDrawerTarget ? structuredClone(selectedDrawerTarget) : {});
    setSelectedPort(selectedDrawerTarget ? structuredClone(selectedDrawerTarget) : {});
  }, [selectedDrawerTarget]);

  useEffect(() => {
    if (!selectedPort || !originalSelectedPort) return;
    const isDiff = !isEqual(selectedPort, originalSelectedPort);
    setIsChanged(isDiff);
  }, [selectedPort, originalSelectedPort]);

  useEffect(() => {
    if (isOpenEditDrawer) {
      getProfileList();
      setIsEdited({});
    }
  }, [isOpenEditDrawer]);

  useEffect(() => {
    if (!selectedPort) return;

    const response = getData(editUuids.getProfileList);
    const responseData = response?.data ?? null;

    if (responseData) {
      if (responseData.hasOwnProperty("code") && responseData.code !== 200) {
        setAlertInfo({
          open: true,
          severity: "error",
          message: responseData.error?.message || "Failed to get the profile list.",
        });
      } else {
        setProfileList(getInitProfileList({
          profileList: responseData,
          selectedPort
        }));
      }

      setIsLoading(false);
    }
  }, [dataMap?.[editUuids.getProfileList], selectedPort]);

  if (!selectedPort) return <></>;

  return (
    <Box>
      {isLoading && <Loading />}

      <Box
        sx={{
          paddingLeft: "25px",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <>
          <HeaderIcon
            state={selectedPort?.state}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                fontWeight: theme.typography.customFontWeight[3],
                fontSize: theme.typography.customFontSize[5],
                color: theme.palette.primary.greyPrimary,
              }}
            >
              {isNeedCombineModel ? selectedPort?.combinePortName : selectedPort?.portName}
            </span>

            {selectedPort?.portIndex && (
              <Box sx={{ display: "flex", gap: 1 }}>
                Port {isNeedCombineModel ? selectedPort?.combinePortIndex : selectedPort?.portIndex}
              </Box>
            )}
          </Box>
        </>
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start",
          py: 2,
          px: 2,
          gap: "20px",
          height: `${selectedPort?.portType === defaultPortTypes.trunk.value ? "auto" : "calc(100vh - 164px)"}`,
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            px: 2,
          }}
        >
          <CollapseCard title="Port">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

              {/* Port Name */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port Name
                </Typography>
                <TextField
                  type="text"
                  disabled={!showEditGeneral}
                  value={isNeedCombineModel ? selectedPort?.combinePortName : selectedPort?.portName}
                  onChange={e => {
                    if (isNeedCombineModel) updatePorts("combinePortName", e.target.value);
                    else updatePorts("portName", e.target.value);
                  }}
                  sx={getInputStyles({
                    theme,
                    isInValid: selectedPort?.portName === "",
                    isEdited,
                    field: "portName"
                  })}
                />
              </StyledBox>

              {/* Port Profile */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port Profile
                </Typography>
                <MenuSelect
                  value={selectedProfile?.value || ""}
                  disabled={!showEditGeneral}
                  onChange={opt => {

                    setProfileList(prevProfileList => {
                      return prevProfileList.map(profile => {
                        return {
                          ...profile,
                          isSelected: profile.id === opt.id,
                        }
                      })
                    });

                    updatePorts("portProfile", opt.id);

                    // Replace by the profile value or not
                    if (!opt.isDefault) {
                      // Non default profile
                      Object.keys(opt).forEach((key) => {
                        if (key !== "name" && key !== "deviceCategory" && key !== "portProfile") {
                          updatePorts(key, opt[key]);
                        }
                      });
                    } else {
                      // Default profile
                      Object.keys(originalSelectedPort).forEach((key) => {
                        if (key !== "name" && key !== "deviceCategory" && key !== "portProfile") {
                          updatePorts(key, originalSelectedPort[key]);
                        }
                      });
                    }

                  }}
                  width="100%"
                  options={profileList}
                  isEditedField={isEdited.portProfile}
                />
              </StyledBox>

              {/* Port State */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port State
                </Typography>

                {
                  isProfileMode &&
                  <RadioGroup
                    row
                    disabled
                    value={selectedProfile?.isPortScheduleMode ? "schedule" : (selectedProfile?.portState ? "enable" : "disable")}
                    sx={{
                      display: "flex",
                      gap: "12px",
                    }}
                  >
                    {["Enable", "Disable", "Schedule"].map((option, idx) => {
                      const value = option.toLowerCase();
                      const checked = value === "schedule" ? selectedProfile?.isPortScheduleMode : (value === "enable" ? (selectedProfile?.portState && !selectedProfile?.isPortScheduleMode) : (!selectedProfile?.portState && !selectedProfile?.isPortScheduleMode));
                      return (
                        <FormControlLabel
                          disabled
                          key={"portStateRadio" + idx}
                          value={value}
                          control={
                            <Radio
                              icon={<UnCheckedIcon />}
                              checkedIcon={<CheckedIcon />}
                              checked={!!checked}
                            />
                          }
                          label={option}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: theme.typography.customFontSize[2],
                              color: theme.palette.primary.greyPrimary,
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                }

                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    disabled={!isEditable || !showEditGeneral}
                    value={selectedPort?.isPortScheduleMode ? "schedule" : (selectedPort?.portState ? "enable" : "disable")}
                    onChange={e => {
                      const val = e.target.value;
                      updatePorts("portState", val === "enable" || val === "schedule");
                      updatePorts("isPortScheduleMode", val === "schedule");
                    }}
                    sx={{
                      display: "flex",
                      gap: "12px",
                    }}
                  >
                    {["Enable", "Disable", "Schedule"].map((option, idx) => {
                      const value = option.toLowerCase();
                      const changed = isEdited.portState != null || isEdited.isPortScheduleMode != null;
                      const checked = value === "schedule" ? selectedPort?.isPortScheduleMode : (value === "enable" ? (selectedPort?.portState && !selectedPort?.isPortScheduleMode) : (!selectedPort?.portState && !selectedPort?.isPortScheduleMode));
                      return (
                        <FormControlLabel
                          disabled={!isEditable || !showEditGeneral}
                          key={"portStateRadio" + idx}
                          value={value}
                          control={
                            <Radio
                              icon={<UnCheckedIcon />}
                              checkedIcon={changed ? <CheckChangedIcon /> : <CheckedIcon />}
                              checked={!!checked}
                            />
                          }
                          label={option}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: theme.typography.customFontSize[2],
                              color: theme.palette.primary.greyPrimary,
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                }
              </StyledBox>

              {isProfileMode && profilePortScheduleOn && (
                <StyledBox sx={{ marginTop: "0", alignItems: "flex-start" }}>
                  <Typography className={mainStyle["collapse-typography"]}></Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <MenuSelect
                      disabled
                      value={selectedProfile?.portSchedule || ""}
                      options={scheduleProfiles}
                      width="100%"
                    />
                    {renderScheduleUsageHint()}
                  </Box>
                </StyledBox>
              )}

              {!isProfileMode && selectedPort?.isPortScheduleMode && (
                <StyledBox sx={{ marginTop: "0", alignItems: "flex-start" }}>
                  <Typography className={mainStyle["collapse-typography"]}></Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <MenuSelect
                      disabled={!isEditable || !showEditGeneral}
                      value={selectedPort?.portSchedule || ""}
                      onChange={opt => {
                        if (opt.value === "Create New Schedule") {
                          setLeaveModalState({ open: true, profileType: "Schedule", page: "schedule" });
                          return;
                        }
                        updatePorts("portSchedule", opt.value);
                      }}
                      options={scheduleOptionsWithLimit}
                      placeholder="Select a Schedule Profile"
                      width="100%"
                      isEditedField={isEdited.portSchedule}
                      pinningOptions={[
                        {
                          value: "Create New Schedule",
                          label: "Create New Schedule",
                          showArrow: true,
                        }
                      ]}
                    />
                    {renderScheduleUsageHint()}
                  </Box>
                </StyledBox>
              )}

              {/* Port Type */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  Port Type
                </Typography>

                {
                  isProfileMode &&
                  <RadioGroup
                    row
                    disabled
                    value={selectedProfile.portType}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {
                      Object.keys(defaultPortTypes).map((key) => {
                        const portType = defaultPortTypes[key];
                        const changed = isEdited.portType != null;

                        return (
                          <FormControlLabel
                            disabled
                            key={portType.key}
                            value={portType.value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={
                                  changed ? <CheckChangedIcon /> : <CheckedIcon />
                                }
                              />
                            }
                            label={portType.label}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </RadioGroup>
                }

                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    disabled={!isEditable || !showEditGeneral}
                    value={selectedPort?.portType}
                    onChange={e => updatePorts("portType", e.target.value)}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {
                      Object.keys(defaultPortTypes).map((key) => {
                        const portType = defaultPortTypes[key];
                        const changed = isEdited.portType != null;

                        return (
                          <FormControlLabel
                            disabled={!isEditable || !showEditGeneral}
                            key={portType.key}
                            value={portType.value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={
                                  changed ? <CheckChangedIcon /> : <CheckedIcon />
                                }
                              />
                            }
                            label={portType.label}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </RadioGroup>
                }
              </StyledBox>

              {/* PVID */}
              <StyledBox>
                <Typography
                  className={mainStyle["collapse-typography"]}
                  sx={{ alignSelf: "flex-start", pt: "6px" }}
                >
                  PVID
                </Typography>

                {
                  isProfileMode &&
                  <TextField
                    disabled
                    type="number"
                    value={selectedProfile?.PVID ?? ""}
                    sx={getInputStyles({
                      theme,
                      isInValid: false,
                      isEdited: false,
                      field: "PVID"
                    })}
                    helperText="(1~4094)"
                    FormHelperTextProps={{
                      sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                    }}
                  />
                }

                {
                  !isProfileMode &&
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      disabled={!isEditable || !showEditGeneral}
                      type="number"
                      value={selectedPort?.PVID ?? ""}
                      helperText="(1~4094)"
                      FormHelperTextProps={{
                        sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                      }}
                      onChange={e => updatePorts("PVID", e.target.value)}
                      onBlur={e => {
                        const num = Number(e.target.value);
                        if (!isNaN(num)) updatePorts("PVID", num);
                      }}
                      sx={getInputStyles({
                        theme,
                        isInValid: !checkPvid(selectedPort?.PVID?.toString().trim()),
                        isEdited,
                        field: "PVID"
                      })}
                    />
                  </Box>
                }
              </StyledBox>

              {
                !isProfileMode &&
                vlanIdsCount.number > 128 &&
                <>
                  <StyledBox sx={{ marginTop: "-1.3rem" }}>
                    <Typography
                      className={mainStyle["collapse-typography"]}
                      sx={{ alignSelf: "flex-start", pt: "6px" }}
                    >
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.palette.primary.redPrimary,
                        fontSize: "0.75rem",
                      }}
                    >
                      Limit reached: Maximum 128 VLANs allowed.
                    </Typography>

                  </StyledBox>
                </>
              }

              {/* Allow VLAN */}
              {
                selectedProfile?.portType === defaultPortTypes.trunk.value &&
                isProfileMode &&
                <StyledBox>
                  <Typography
                    className={mainStyle["collapse-typography"]}
                    sx={{ alignSelf: "flex-start", pt: "6px" }}
                  >
                    Allow VLAN
                  </Typography>

                  <TextField
                    disabled
                    type="text"
                    placeholder="e.g. 1,4-7"
                    value={selectedProfile?.allowVlan}
                    sx={getInputStyles({
                      theme,
                      isInValid: false,
                      isEdited: false,
                      field: "allowVlan"
                    })}
                    helperText={`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                    FormHelperTextProps={{
                      sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                    }}
                  />

                </StyledBox>
              }

              {
                selectedPort?.portType === defaultPortTypes.trunk.value &&
                !isProfileMode &&
                <StyledBox>
                  <Typography
                    className={mainStyle["collapse-typography"]}
                    sx={{ alignSelf: "flex-start", pt: "6px" }}
                  >
                    Allow VLAN
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      disabled={!isEditable || !showEditGeneral}
                      type="text"
                      placeholder="e.g. 1, 4-7"
                      helperText={`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`} // (1~4094)
                      FormHelperTextProps={{
                        sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                      }}
                      value={selectedPort?.allowVlan ?? ""}
                      onChange={e => updatePorts("allowVlan", e.target.value)}
                      sx={getInputStyles({
                        theme,
                        isInValid: isVlanInvalid,
                        isEdited,
                        field: "allowVlan"
                      })}
                    />
                  </Box>

                </StyledBox>
              }

              {
                !isProfileMode &&
                vlanIdsCount.number > 128 &&
                <>
                  <StyledBox sx={{ marginTop: "-1.3rem" }}>
                    <Typography
                      className={mainStyle["collapse-typography"]}
                      sx={{ alignSelf: "flex-start", pt: "6px" }}
                    >
                    </Typography>
                    <Typography
                      sx={{
                        color: theme.palette.primary.redPrimary,
                        fontSize: "0.75rem",
                      }}
                    >
                      Limit reached: Maximum 128 VLANs allowed.
                    </Typography>

                  </StyledBox>
                </>
              }

              {/* Link Speed */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  gap: 2,
                }}
              >

                {/* Copper speed */}
                {
                  isCopper &&
                  linkSpeeds.copperList.length > 0 &&
                  (
                    <>
                      {
                        isProfileMode &&
                        <StyledBox>
                          <Typography className={mainStyle["collapse-typography"]}>
                            Port Speed
                          </Typography>
                          <MenuSelect
                            disabled
                            value={selectedProfile?.speed}
                            options={linkSpeeds.copperList}
                            width="100%"
                          />
                        </StyledBox>
                      }

                      {
                        !isProfileMode &&
                        <StyledBox>
                          <Typography className={mainStyle["collapse-typography"]}>
                            Port Speed
                          </Typography>

                          <MenuSelect
                            disabled={!isEditable || !showEditGeneral}
                            value={selectedPort?.speed || ""}
                            onChange={opt => {
                              updatePorts("speed", opt.value);

                              setLinkSpeeds(prevLinkSpeeds => {
                                return {
                                  ...prevLinkSpeeds,
                                  copperList: prevLinkSpeeds.copperList.map((speed, index) => {
                                    return {
                                      ...speed,
                                      isSelected: speed.value === opt.value ?? index === 0
                                    }
                                  })
                                }
                              });
                            }}
                            options={linkSpeeds.copperList}
                            width="100%"
                            isEditedField={isEdited.speed}
                          />
                        </StyledBox>
                      }
                    </>
                  )
                }

                {/* Fiber speed */}
                {
                  isFiber &&
                  linkSpeeds.fiberList.length > 0 &&
                  (
                    <>
                      {
                        isProfileMode &&
                        <StyledBox>
                          <Typography className={mainStyle["collapse-typography"]}>
                            Port Speed
                          </Typography>
                          <MenuSelect
                            disabled
                            value={selectedProfile?.speed}
                            options={linkSpeeds.fiberList}
                            width="100%"
                          />
                        </StyledBox>
                      }
                      {
                        !isProfileMode &&
                        <StyledBox>
                          <Typography className={mainStyle["collapse-typography"]}>
                            Port Speed
                          </Typography>

                          <MenuSelect
                            disabled={!isEditable || !showEditGeneral}
                            value={selectedPort?.comboSfpSpeed || ""}
                            onChange={opt => {
                              updatePorts("comboSfpSpeed", opt.value);

                              setLinkSpeeds(prevLinkSpeeds => {
                                return {
                                  ...prevLinkSpeeds,
                                  fiberList: prevLinkSpeeds.fiberList.map((comboSfpSpeed, index) => {
                                    return {
                                      ...comboSfpSpeed,
                                      isSelected: comboSfpSpeed.value === opt.value ?? index === 0
                                    }
                                  })
                                }
                              });
                            }}
                            options={linkSpeeds.fiberList}
                            width="100%"
                            isEditedField={isEdited.comboSfpSpeed}
                          />
                        </StyledBox>
                      }
                    </>
                  )
                }

                {/* Operation */}
                <StyledBox>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Operation
                  </Typography>
                  {isProfileMode && (
                    <MenuSelect
                      disabled
                      value={selectedProfile?.operation?.type || "switching"}
                      options={defaultOperationList}
                      width="100%"
                    />
                  )}
                  {!isProfileMode && (
                    <MenuSelect
                      disabled={!isEditable || !showEditGeneral}
                      value={selectedPort.operation?.type || "switching"}
                      onChange={(opt) => {
                        const newType = opt.value;
                        const newGroupId = newType === "aggregation"
                          ? getAvailableAggregateGroupId(defaultConfig.portConfigs || [], selectedPort.operation?.aggregateGroupId, selectedPort.aggregation)
                          : null;
                        updatePorts("operation", {
                          type: newType,
                          mirrorPort: newType === "mirroring" ? (selectedPort.operation?.mirrorPort || []) : [],
                          mirrorMode: newType === "mirroring" ? (selectedPort.operation?.mirrorMode || "both") : "",
                          aggregatePort: newType === "aggregation"
                            ? (selectedPort.operation?.aggregatePort || []).map(Number).filter(idx => idx !== Number(selectedPort.portIndex))
                            : [],
                          aggregateType: newType === "aggregation"
                            ? (["lacp", "static"].includes((selectedPort.operation?.aggregateType || "").toLowerCase())
                              ? selectedPort.operation.aggregateType.toLowerCase()
                              : "lacp")
                            : "",
                          aggregateGroupId: newGroupId
                        });
                      }}
                      options={defaultOperationList}
                      width="100%"
                      isEditedField={isEdited.operation}
                    />
                  )}
                </StyledBox>

                {/* Mirroring Port Selector Grid */}
                {!isProfileMode && selectedPort.operation?.type === "mirroring" && (
                  <Box sx={{ pl: "142px", width: "100%", boxSizing: "border-box", mb: 2 }}>
                    {/* Legend */}
                    <Box sx={{ display: "flex", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
                      <Box sx={legendItemStyle}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: "#ffffff", border: "1px solid #DDDDDD", borderRadius: "2px" }} />
                        <Typography variant="caption">Unselected</Typography>
                      </Box>
                      <Box sx={legendItemStyle}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: theme?.palette?.primary?.main, borderRadius: "2px" }} />
                        <Typography variant="caption">Source Port</Typography>
                      </Box>
                      <Box sx={legendItemStyle}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: theme?.palette?.primary?.blueSecound, borderRadius: "2px" }} />
                        <Typography variant="caption">Destination Port</Typography>
                      </Box>
                    </Box>
                    {/* Grid */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: 2 }}>
                      {devicePorts.map((p) => {
                        const portIdx = p.portIndex;
                        const isSourcePort = Number(selectedPort.portIndex) === Number(portIdx);
                        let status = "unselected";
                        if (isSourcePort) {
                          status = "source";
                        } else if ((selectedPort.operation?.mirrorPort || []).map(Number).includes(Number(portIdx))) {
                          status = "destination";
                        }

                        return (
                          <Box
                            key={portIdx}
                            sx={portBoxStyle(status, theme)}
                            onClick={() => {
                              if (!isEditable || !showEditGeneral) return;
                              if (isSourcePort) return;
                              const currentMirror = (selectedPort.operation?.mirrorPort || []).map(Number);
                              let updatedMirror;
                              if (status === "destination") {
                                updatedMirror = currentMirror.filter(id => id !== Number(portIdx));
                              } else {
                                updatedMirror = [...currentMirror, Number(portIdx)].sort((a, b) => a - b);
                              }
                              updatePorts("operation", {
                                ...selectedPort.operation,
                                mirrorPort: updatedMirror
                              });
                            }}
                          >
                            {portIdx}
                          </Box>
                        );
                      })}
                    </Box>
                    {/* Mirror Mode Dropdown */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography sx={{ minWidth: "70px", fontSize: 13, color: "text.secondary" }}>Mirror Mode</Typography>
                      <MenuSelect
                        disabled={!isEditable || !showEditGeneral}
                        value={selectedPort.operation?.mirrorMode || "both"}
                        onChange={(opt) => {
                          updatePorts("operation", {
                            ...selectedPort.operation,
                            mirrorMode: opt.value
                          });
                        }}
                        options={[
                          { label: "Tx", value: "egress" },
                          { label: "Rx", value: "ingress" },
                          { label: "Both", value: "both" }
                        ]}
                        width="150px"
                      />
                    </Box>
                  </Box>
                )}

                {/* Aggregation Port Selector Grid */}
                {!isProfileMode && selectedPort.operation?.type === "aggregation" && (
                  <Box sx={{ pl: "142px", width: "100%", boxSizing: "border-box", mb: 2 }}>
                    {/* Legend */}
                    <Box sx={{ display: "flex", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
                      <Box sx={legendItemStyle}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: "#ffffff", border: "1px solid #DDDDDD", borderRadius: "2px" }} />
                        <Typography variant="caption">Unselected</Typography>
                      </Box>
                      <Box sx={legendItemStyle}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: theme?.palette?.primary?.main || "#172664", borderRadius: "2px" }} />
                        <Typography variant="caption">Selected</Typography>
                      </Box>
                    </Box>
                    {/* Grid */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: 0.5 }}>
                      {(() => {
                        // aggregateGroupId 為 null 代表組數額度已滿, 這個 port 沒辦法建立新群組,
                        // 整個選取格都要鎖住 (跟「這個群組本身已滿員」是不同狀況).
                        const isNoGroupAvailable = selectedPort.operation?.aggregateGroupId == null;
                        const maxAggregationPorts = selectedPort.aggregation;
                        const currentGroupSize = 1 + (selectedPort.operation?.aggregatePort || []).length;
                        const isGroupFull = maxAggregationPorts != null && currentGroupSize >= maxAggregationPorts;

                        return devicePorts.map((p) => {
                        const portIdx = p.portIndex;
                        const isSourcePort = Number(selectedPort.portIndex) === Number(portIdx);
                        const isSelectedInGrid = (selectedPort.operation?.aggregatePort || []).map(Number).includes(Number(portIdx));
                        const isOtherAggregated = isPortInOtherAggregateGroup(p);
                        let status = "unselected";
                        if (isSourcePort || isSelectedInGrid) {
                          status = "selected";
                        } else if (isNoGroupAvailable) {
                          status = "noGroup";
                        } else if (isOtherAggregated) {
                          status = "disabled";
                        } else if (isGroupFull) {
                          status = "full";
                        }

                        const renderBox = (
                          <Box
                            key={portIdx}
                            sx={portBoxStyle(status === "full" || status === "noGroup" ? "disabled" : status, theme)}
                            onClick={() => {
                              if (!isEditable || !showEditGeneral) return;
                              if (status === "disabled" || status === "full" || status === "noGroup" || isSourcePort) return;
                              const currentPorts = (selectedPort.operation?.aggregatePort || []).map(Number).filter(id => id !== Number(selectedPort.portIndex));
                              let updatedPorts;
                              if (isSelectedInGrid) {
                                updatedPorts = currentPorts.filter((id) => id !== Number(portIdx));
                              } else {
                                updatedPorts = [...currentPorts, Number(portIdx)].sort((a, b) => a - b);
                              }
                              updatePorts("operation", {
                                ...selectedPort.operation,
                                aggregatePort: updatedPorts
                              });
                            }}
                          >
                            {portIdx}
                          </Box>
                        );

                        if (status === "disabled" || status === "full" || status === "noGroup") {
                          let title = "This port is part of a different aggregation group.";
                          if (status === "full") title = "Maximum ports reached.";
                          if (status === "noGroup") title = "Maximum groups reached.";
                          return (
                            <Tooltip
                              key={portIdx}
                              arrow
                              title={title}
                            >
                              <span style={{ display: "inline-block" }}>
                                {renderBox}
                              </span>
                            </Tooltip>
                          );
                        }
                        return renderBox;
                        });
                      })()}
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ color: "orange" }}>
                        All selected ports will be synchronized with these settings.
                      </Typography>
                    </Box>
                    {/* Aggregating Mode Dropdown */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
                      <Typography sx={{ minWidth: "110px", fontSize: 13, color: "text.secondary" }}>Aggregating Mode</Typography>
                      <MenuSelect
                        disabled={!isEditable || !showEditGeneral}
                        value={(selectedPort.operation?.aggregateType || "lacp").toLowerCase()}
                        onChange={(opt) => {
                          updatePorts("operation", {
                            ...selectedPort.operation,
                            aggregateType: opt.value.toLowerCase()
                          });
                        }}
                        options={[
                          { label: "LACP", value: "lacp" },
                          { label: "Static", value: "static" }
                        ]}
                        width="150px"
                      />
                    </Box>
                    {/* Group ID */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography sx={{ minWidth: "110px", fontSize: 13, color: "text.secondary" }}>Group ID</Typography>
                      {selectedPort.operation?.aggregateGroupId != null ? (
                        <Typography sx={{ fontSize: 14, fontWeight: "bold" }}>
                          {selectedPort.operation.aggregateGroupId}
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: 14, color: "error.main" }}>
                          Maximum groups reached.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

              </Box>
            </Box>
          </CollapseCard>

          {
            isSelectedPortSupportPoE &&
            <CollapseCard title="PoE">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <StyledBox>
                  <Typography className={mainStyle["collapse-typography"]}>
                    PoE Status
                  </Typography>

                  {
                    isProfileMode &&
                    <RadioGroup
                      row
                      disabled
                      value={selectedProfile?.isPoeScheduleMode ? "schedule" : (selectedProfile?.poeState ? "enable" : "disable")}
                      sx={{
                        display: "flex",
                        gap: "12px",
                      }}
                    >
                      {["Enable", "Disable", "Schedule"].map((option, idx) => {
                        const value = option.toLowerCase();
                        const checked = value === "schedule" ? selectedProfile?.isPoeScheduleMode : (value === "enable" ? (selectedProfile?.poeState && !selectedProfile?.isPoeScheduleMode) : (!selectedProfile?.poeState && !selectedProfile?.isPoeScheduleMode));
                        return (
                          <FormControlLabel
                            disabled
                            key={"poeStateRadio" + idx}
                            value={value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={<CheckedIcon />}
                                checked={!!checked}
                              />
                            }
                            label={option}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })}
                    </RadioGroup>
                  }

                  {
                    !isProfileMode &&
                    <RadioGroup
                      row
                      disabled={!isEditable || !showEditGeneral}
                      value={selectedPort?.isPoeScheduleMode ? "schedule" : (selectedPort?.poeState ? "enable" : "disable")}
                      onChange={e => {
                        const val = e.target.value;
                        updatePorts("poeState", val === "enable" || val === "schedule");
                        updatePorts("isPoeScheduleMode", val === "schedule");
                      }}
                      sx={{
                        display: "flex",
                        gap: "12px",
                      }}
                    >
                      {["Enable", "Disable", "Schedule"].map((option, idx) => {
                        const value = option.toLowerCase();
                        const changed = isEdited.poeState != null || isEdited.isPoeScheduleMode != null;
                        const checked = value === "schedule" ? selectedPort?.isPoeScheduleMode : (value === "enable" ? (selectedPort?.poeState && !selectedPort?.isPoeScheduleMode) : (!selectedPort?.poeState && !selectedPort?.isPoeScheduleMode));
                        return (
                          <FormControlLabel
                            disabled={!isEditable || !showEditGeneral}
                            key={"poeStateRadio" + idx}
                            value={value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={changed ? <CheckChangedIcon /> : <CheckedIcon />}
                                checked={!!checked}
                              />
                            }
                            label={option}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })}
                    </RadioGroup>
                  }
                </StyledBox>

                {isProfileMode && profilePoeScheduleOn && (
                  <StyledBox sx={{ marginTop: "0", alignItems: "flex-start" }}>
                    <Typography className={mainStyle["collapse-typography"]}></Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <MenuSelect
                        disabled
                        value={selectedProfile?.poeSchedule || ""}
                        options={scheduleProfiles}
                        width="100%"
                      />
                      {renderScheduleUsageHint()}
                    </Box>
                  </StyledBox>
                )}

                {!isProfileMode && selectedPort?.isPoeScheduleMode && (
                  <StyledBox sx={{ marginTop: "0", alignItems: "flex-start" }}>
                    <Typography className={mainStyle["collapse-typography"]}></Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <MenuSelect
                        disabled={!isEditable || !showEditGeneral}
                        value={selectedPort?.poeSchedule || ""}
                        onChange={opt => {
                          if (opt.value === "Create New Schedule") {
                            setLeaveModalState({ open: true, profileType: "Schedule", page: "schedule" });
                            return;
                          }
                          updatePorts("poeSchedule", opt.value);
                        }}
                        options={scheduleOptionsWithLimit}
                        placeholder="Select a Schedule Profile"
                        width="100%"
                        isEditedField={isEdited.poeSchedule}
                        pinningOptions={[
                          {
                            value: "Create New Schedule",
                            label: "Create New Schedule",
                            showArrow: true,
                          }
                        ]}
                      />
                      {renderScheduleUsageHint()}
                    </Box>
                  </StyledBox>
                )}
              </Box>
            </CollapseCard>
          }

          <CollapseCard title="Advance">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  DDP
                </Typography>

                {
                  isProfileMode &&
                  <RadioGroup
                    disabled
                    row
                    value={selectedProfile?.ddpState ? "enable" : "disable"}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {
                      Object.keys(defaultStateTypes).map((key) => {
                        const portState = defaultStateTypes[key];
                        const changed = isEdited.portState != null;

                        return (
                          <FormControlLabel
                            disabled
                            key={portState.key}
                            value={portState.value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={
                                  changed ? <CheckChangedIcon /> : <CheckedIcon />
                                }
                              />
                            }
                            label={portState.label}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </RadioGroup>
                }

                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    value={selectedPort?.ddpState ? "enable" : "disable"}
                    onChange={e => updatePorts("ddpState", e.target.value === "enable")}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {
                      Object.keys(defaultStateTypes).map((key) => {
                        const portState = defaultStateTypes[key];
                        const changed = isEdited.portState != null;

                        return (
                          <FormControlLabel
                            disabled={!isEditable || !showEditGeneral}
                            key={portState.key}
                            value={portState.value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={
                                  changed ? <CheckChangedIcon /> : <CheckedIcon />
                                }
                              />
                            }
                            label={portState.label}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </RadioGroup>
                }
              </StyledBox>

              {/* LBD */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]}>
                  LBD
                </Typography>

                {
                  isProfileMode &&
                  <RadioGroup
                    disabled
                    row
                    value={selectedProfile?.lbdState ? "enable" : "disable"}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {
                      Object.keys(defaultStateTypes).map((key) => {
                        const portState = defaultStateTypes[key];
                        const changed = isEdited.portState != null;

                        return (
                          <FormControlLabel
                            disabled
                            key={portState.key}
                            value={portState.value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={
                                  changed ? <CheckChangedIcon /> : <CheckedIcon />
                                }
                              />
                            }
                            label={portState.label}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </RadioGroup>
                }

                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    value={selectedPort?.lbdState ? "enable" : "disable"}
                    onChange={e => updatePorts("lbdState", e.target.value === "enable")}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {
                      Object.keys(defaultStateTypes).map((key) => {
                        const portState = defaultStateTypes[key];
                        const changed = isEdited.portState != null;

                        return (
                          <FormControlLabel
                            disabled={!isEditable || !showEditGeneral || selectedPort.rstpState === true}
                            key={portState.key}
                            value={portState.value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={
                                  changed ? <CheckChangedIcon /> : <CheckedIcon />
                                }
                              />
                            }
                            label={portState.label}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })
                    }
                  </RadioGroup>
                }
              </StyledBox>

              {/* RSTP */}
              <StyledBox>
                <Typography className={mainStyle["collapse-typography"]} sx={{ display: "flex", alignItems: "center" }}>
                  RSTP
                  <Tooltip title="When RSTP is enabled as the primary loop prevention mechanism, LBD should not be enabled." placement="top">
                    <Box
                      component="span"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: 16,
                        ml: "5px",
                        "& img": {
                          content: `url(${icon_snmp})`,
                        },
                        "&:hover img": {
                          content: `url(${icon_snmp_hover})`,
                        },
                      }}
                    >
                      <img src={icon_snmp} alt="" style={{ width: 16, height: 16, cursor: "pointer" }} />
                    </Box>
                  </Tooltip>
                </Typography>
                {
                  isProfileMode &&
                  <RadioGroup
                    disabled
                    row
                    value={selectedProfile?.rstpState ? "enable" : "disable"}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {["Enable", "Disable"].map((option, idx) => {
                      const value = option.toLowerCase();
                      const checked = value === "enable" ? selectedProfile?.rstpState : !selectedProfile?.rstpState;
                      return (
                        <FormControlLabel
                          disabled
                          key={"profileRstpRadioButton" + idx}
                          value={value}
                          control={
                            <Radio
                              icon={<UnCheckedIcon />}
                              checkedIcon={<CheckedIcon />}
                              checked={!!checked}
                            />
                          }
                          label={option}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: theme.typography.customFontSize[2],
                              color: theme.palette.primary.greyPrimary,
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                }
                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    disabled={!showEditGeneral}
                    value={selectedPort.rstpState ? "enable" : "disable"}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {["Enable", "Disable"].map((option, idx) => {
                      const value = option.toLowerCase();
                      const changed = isEdited.rstpState != null;
                      const checked = value === "enable" ? selectedPort.rstpState : !selectedPort.rstpState;
                      return (
                        <FormControlLabel
                          disabled={!isEditable || !showEditGeneral}
                          key={"rstpRadioButton" + idx}
                          value={value}
                          onClick={() => {
                            if (!isEditable || !showEditGeneral) return;
                            const isEnable = value === "enable";
                            updatePorts("rstpState", isEnable);
                            if (isEnable) {
                              updatePorts("lbdState", false);
                            } else {
                              updatePorts("stpState", false);
                            }
                          }}
                          control={
                            <Radio
                              icon={<UnCheckedIcon />}
                              checkedIcon={changed ? <CheckChangedIcon /> : <CheckedIcon />}
                              checked={!!checked}
                            />
                          }
                          label={option}
                          sx={{
                            "& .MuiFormControlLabel-label": {
                              fontSize: theme.typography.customFontSize[2],
                              color: theme.palette.primary.greyPrimary,
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                }
              </StyledBox>

              {/* STP Guard */}
              {((isProfileMode ? selectedProfile?.rstpState : selectedPort?.rstpState) === true) && (
                <StyledBox>
                  <Typography className={mainStyle["collapse-typography"]}>
                    STP Guard
                  </Typography>
                  {
                    isProfileMode &&
                    <RadioGroup
                      disabled
                      row
                      value={selectedProfile?.stpState ? "enable" : "disable"}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "80px auto",
                        columnGap: "20px",
                      }}
                    >
                      {["Enable", "Disable"].map((option, idx) => {
                        const value = option.toLowerCase();
                        const checked = value === "enable" ? selectedProfile?.stpState : !selectedProfile?.stpState;
                        return (
                          <FormControlLabel
                            disabled
                            key={"profileStpRadioButton" + idx}
                            value={value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={<CheckedIcon />}
                                checked={!!checked}
                              />
                            }
                            label={option}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })}
                    </RadioGroup>
                  }
                  {
                    !isProfileMode &&
                    <RadioGroup
                      row
                      disabled={!showEditGeneral}
                      value={selectedPort.stpState ? "enable" : "disable"}
                      onChange={(e) =>
                        updatePorts("stpState", e.target.value === "enable")
                      }
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "80px auto",
                        columnGap: "20px",
                      }}
                    >
                      {["Enable", "Disable"].map((option, idx) => {
                        const value = option.toLowerCase();
                        const changed = isEdited.stpState != null;
                        const checked = value === "enable" ? selectedPort.stpState : !selectedPort.stpState;
                        return (
                          <FormControlLabel
                            disabled={!isEditable || !showEditGeneral}
                            key={"stpRadioButton" + idx}
                            value={value}
                            control={
                              <Radio
                                icon={<UnCheckedIcon />}
                                checkedIcon={changed ? <CheckChangedIcon /> : <CheckedIcon />}
                                checked={!!checked}
                              />
                            }
                            label={option}
                            sx={{
                              "& .MuiFormControlLabel-label": {
                                fontSize: theme.typography.customFontSize[2],
                                color: theme.palette.primary.greyPrimary,
                              },
                            }}
                          />
                        );
                      })}
                    </RadioGroup>
                  }
                </StyledBox>
              )}

              {/* Access Policy */}
              {isProfileMode && selectedProfile?.portType === "access" && (
                <>
                  <StyledBox>
                    <Typography className={mainStyle["collapse-typography"]}>
                      Access Policy
                      {isUnityProSubscribed || !selectedProfile?.accessPolicy?.enableState ? (
                        <ProColorIcon aria-label="Pro" style={proIconStyle} />
                      ) : (
                        <Tooltip
                          title="Pro feature disabled"
                          placement="bottom"
                          arrow
                        >
                          <ProOrangeOutlineIcon aria-label="Pro" style={proIconStyle} />
                        </Tooltip>
                      )}
                    </Typography>
                    <MenuSelect
                      disabled
                      value={
                        selectedProfile?.accessPolicy?.enableState
                          ? selectedProfile.accessPolicy.type
                          : accessPolicyTypeDefinition.DISABLED.value
                      }
                      options={defaultAccessPolicyTypeList}
                      width="100%"
                    />
                  </StyledBox>

                  {selectedProfile?.accessPolicy?.enableState &&
                    (selectedProfile.accessPolicy.type === accessPolicyTypeDefinition.STATIC.value ||
                      selectedProfile.accessPolicy.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value) && (
                      <MacWhitelistSection
                        macProfile={selectedProfile.accessPolicy.macList || null}
                        macProfileOptions={macProfiles.map((p) => ({ label: p.name, value: p.id, macCount: p.macList?.length ?? p.macAddresses?.length ?? 0 }))}
                        macProfilesRaw={macProfiles}
                        showEditGeneral={false}
                        showDynamicSize={selectedProfile.accessPolicy.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value}
                        dynamicSize={selectedProfile.accessPolicy.dynamicSize}
                        isEdited={isEdited}
                      />
                    )}

                  {selectedProfile?.accessPolicy?.enableState &&
                    selectedProfile.accessPolicy.type === accessPolicyTypeDefinition["8021X_PROFILE"].value && (
                      <RadiusProfileSection
                        radiusProfile={selectedProfile.accessPolicy.radiusProfile || null}
                        radiusProfileOptions={radiusProfiles.map((p) => ({ label: p.name, value: p.id }))}
                        radiusProfilesRaw={radiusProfiles}
                        showEditGeneral={false}
                      />
                    )}
                </>
              )}

              {!isProfileMode && selectedPort.portType === "access" && (
                <>
                  <StyledBox>
                    <Typography className={mainStyle["collapse-typography"]}>
                      Access Policy
                      {isUnityProSubscribed || !selectedPort.accessPolicy?.enableState ? (
                        <ProColorIcon aria-label="Pro" style={proIconStyle} />
                      ) : (
                        <Tooltip
                          title="Pro feature disabled"
                          placement="bottom"
                          arrow
                        >
                          <ProOrangeOutlineIcon aria-label="Pro" style={proIconStyle} />
                        </Tooltip>
                      )}
                    </Typography>
                    <MenuSelect
                      disabled={!isEditable || !showEditGeneral}
                      value={
                        selectedPort.accessPolicy?.enableState
                          ? selectedPort.accessPolicy.type
                          : accessPolicyTypeDefinition.DISABLED.value
                      }
                      onChange={(opt) => {
                        updatePorts("accessPolicy", {
                          ...selectedPort.accessPolicy,
                          enableState: opt.value !== accessPolicyTypeDefinition.DISABLED.value,
                          type: opt.value === accessPolicyTypeDefinition.DISABLED.value
                            ? accessPolicyTypeDefinition.STATIC.value
                            : opt.value,
                        });
                      }}
                      options={defaultAccessPolicyTypeList}
                      width="100%"
                      isEditedField={isEdited.accessPolicy}
                    />
                  </StyledBox>

                  {selectedPort.accessPolicy?.enableState &&
                    (selectedPort.accessPolicy.type === accessPolicyTypeDefinition.STATIC.value ||
                      selectedPort.accessPolicy.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value) && (
                      <MacWhitelistSection
                        macProfile={selectedPort.accessPolicy.macList || null}
                        onChangeMacProfile={(opt) => {
                          if (opt.value === "Edit/Create New Profile") {
                            setLeaveModalState({ open: true, profileType: "Mac Address", page: "mac-address" });
                            return;
                          }
                          updatePorts("accessPolicy", {
                            ...selectedPort.accessPolicy,
                            macList: opt.value,
                          });
                        }}
                        macProfileOptions={macProfiles.map((p) => ({ label: p.name, value: p.id, macCount: p.macList?.length ?? p.macAddresses?.length ?? 0 }))}
                        macProfilesRaw={macProfiles}
                        showEditGeneral={isEditable && showEditGeneral}
                        showDynamicSize={selectedPort.accessPolicy.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value}
                        dynamicSize={selectedPort.accessPolicy.dynamicSize}
                        onChangeDynamicSize={(val) => {
                          updatePorts("accessPolicy", {
                            ...selectedPort.accessPolicy,
                            dynamicSize: val === "" ? null : parseInt(val, 10),
                          });
                        }}
                      />
                    )}

                  {selectedPort.accessPolicy?.enableState &&
                    selectedPort.accessPolicy.type === accessPolicyTypeDefinition["8021X_PROFILE"].value && (
                      <RadiusProfileSection
                        radiusProfile={selectedPort.accessPolicy.radiusProfile || null}
                        onChangeRadiusProfile={(opt) => {
                          if (opt.value === "Edit/Create New Profile") {
                            setLeaveModalState({ open: true, profileType: "RADIUS Server", page: "radius-server" });
                            return;
                          }
                          updatePorts("accessPolicy", {
                            ...selectedPort.accessPolicy,
                            radiusProfile: opt.value,
                          });
                        }}
                        radiusProfileOptions={radiusProfiles.map((p) => ({ label: p.name, value: p.id }))}
                        radiusProfilesRaw={radiusProfiles}
                        showEditGeneral={isEditable && showEditGeneral}
                      />
                    )}
                </>
              )}

            </Box>
          </CollapseCard>
        </Box>

        {
          isChanged && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                padding: "16px",
                backgroundColor: "primary.lightBlue",
                mt: "auto",
                zIndex: (theme) => theme.zIndex.drawer + 1,
              }}
            >
              <OutlineButton
                sx={{ marginRight: "20px" }}
                width="100px"
                height="32px"
                onClick={onCancel}
              >
                Cancel
              </OutlineButton>
              <SolidButton
                width="100px"
                height="32px"
                disabled={isFormInvalid()}
                onClick={onApply}
              >
                Apply
              </SolidButton>
            </Box>
          )
        }
      </Box>
      <AggregationConfirmModal
        showAggregationConfirmModal={showAggregationConfirmModal}
        setShowAggregationConfirmModal={setShowAggregationConfirmModal}
        onProceedCallback={handleProceedApply}
      />
      <LeaveConfirmModal
        isOpen={leaveModalState.open}
        onClose={() => setLeaveModalState(prev => ({ ...prev, open: false }))}
        leaveCallback={handleLeaveConfirm}
        profileType={leaveModalState.profileType}
      />
      <LinkModal
        open={isLinkModalOpen}
        transparentBackdrop
        onClose={() => setIsLinkModalOpen(false)}
        onLinkClick={() => {
          setIsLinkModalOpen(false);
          const isMspScope = auth?.realm?.hasManageOrg === true && !auth?.orgs?.id;
          navigate(isMspScope ? "/msp/license" : "/license");
        }}
      />
    </Box>
  );
};

export default ContentSwitch;